/* eslint-disable simple-import-sort/imports */
import PropTypes from "prop-types";
import React, { Fragment } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import FormCheckbox from "../../components/form/FormCheckbox";
import FormCheckboxDropdown from "../../components/form/FormCheckboxDropdown";
import FormDropdown from "../../components/form/FormDropdown";
import ModalDescription from "../../components/modal/ModalDescription";
import ModalField from "../../components/modal/ModalField";
import ModalGroup from "../../components/modal/ModalGroup";
import ModalRow from "../../components/modal/ModalRow";
import NarrativeLabelList from "../../components/narrativeLabelList/NarrativeLabelList";
import SettingsOptionList from "../../components/optionsList/SettingsOptionList";
import { NarrativeNumberingDropdownOptions } from "../../constants/narrativeNumberingOptions";
import batchOperations from "../../modules/batchOperations";
import { PropTypeShapes } from "../../types";
import { mergeDispatchToPropsWithExtraProps, wrapEditingComponent } from "../../utils/codeSplit";
import HelpIcon from "../../components/interface/HelpIcon";
import Icon from "../../components/interface/Icon";

//#!if readonly !== true
// import { narrativeSettingsOperations } from "../../modules/definition/narrativeSettings";
//#!endif

import "./settingsModal.scss";
import preprocessorQuery from "../../utils/preprocessorQuery";

const isMobile = preprocessorQuery.isMobile();

const NARRATIVE_LABELS_VIEW_ID = "SETTINGS_NARRATIVE_LABELS_VIEW_ID";

const autoAssignTooltipText = `When set to automatic, an item's section type will be selected from the relevant labels in descending order, based on its item type and parent`;

const narrativeNumberingOptions = () => {
  return NarrativeNumberingDropdownOptions.map(o => ({
    label: o.name,
    value: o.identifier,
    description: o.description
  }));
};

const renderNarrativeLabelFieldsMobile = (
  narrativeLabels,
  onChangeLabel,
  onChangeAutoForFolder,
  onChangeAutoForOther,
  onAdd,
  onDelete,
  onMove
) => {
  const autoFolderOptions = narrativeLabels.map(o => {
    return {
      label: o.label,
      value: o.id,
      selected: o.autoForFolder
    };
  });

  const autoOtherOptions = narrativeLabels.map(o => {
    return {
      label: o.label,
      value: o.id,
      selected: o.autoForOther
    };
  });

  return (
    <Fragment>
      <ModalGroup>
        <ModalRow>
          <SettingsOptionList
            items={narrativeLabels}
            showAdd={true}
            onRenameItem={onChangeLabel}
            onDeleteItem={onDelete}
            onAddItem={onAdd}
            viewId={NARRATIVE_LABELS_VIEW_ID}
            onMoveItemAtIndex={onMove}
            useContainerWidth={true}
          />
        </ModalRow>
      </ModalGroup>
      <ModalGroup label="Automatic label assignment">
        <ModalDescription>
          <div className="narrativeSectionDescription">
            Select which sections can be auto-assigned to Narrative Folders and other item types
            <HelpIcon tooltip={autoAssignTooltipText} />
          </div>
        </ModalDescription>
        <ModalRow>
          <ModalField label={"Sections auto-assigned to narrative folders"}>
            <FormCheckboxDropdown options={autoFolderOptions} onChange={onChangeAutoForFolder} />
          </ModalField>
        </ModalRow>
        <ModalRow>
          <ModalField label={"Sections auto-assigned to other types"}>
            <FormCheckboxDropdown options={autoOtherOptions} onChange={onChangeAutoForOther} />
          </ModalField>
        </ModalRow>
      </ModalGroup>
    </Fragment>
  );
};

const renderNarrativeLabelFieldsNormal = (
  narrativeLabels,
  onChangeLabel,
  onChangeAutoForFolder,
  onChangeAutoForOther,
  onAdd,
  onDelete,
  onMove
) => {
  return (
    <NarrativeLabelList
      items={narrativeLabels}
      showAdd={true}
      onRenameItem={onChangeLabel}
      onChangeAutoForFolder={onChangeAutoForFolder}
      onChangeAutoForOther={onChangeAutoForOther}
      onDeleteItem={onDelete}
      onAddItem={onAdd}
      viewId={NARRATIVE_LABELS_VIEW_ID}
      onMoveItemAtIndex={onMove}
      useContainerWidth={true}
    />
  );
};

const renderNarrativeLabelFields = (
  narrativeLabels,
  onChangeLabel,
  onChangeAutoForFolder,
  onChangeAutoForOther,
  onAdd,
  onDelete,
  onMove
) => {
  let controls = null;
  //#!if mobile === true
// controls = renderNarrativeLabelFieldsMobile(
// narrativeLabels,
// onChangeLabel,
// onChangeAutoForFolder,
// onChangeAutoForOther,
// onAdd,
// onDelete,
// onMove
// );
  //#!else
  controls = renderNarrativeLabelFieldsNormal(
    narrativeLabels,
    onChangeLabel,
    onChangeAutoForFolder,
    onChangeAutoForOther,
    onAdd,
    onDelete,
    onMove
  );
  //#!endif

  return (
    <Fragment>
      <ModalDescription>
        <div className="narrativeSectionDescription">
          <Icon icon="statusOpen2" color="iconMid" className="narrativeSectionBullet" />
          List section types from largest to smallest (e.g. Part &gt; Chapter &gt; Scene)
        </div>
        <div className="narrativeSectionDescription">
          <Icon icon="statusOpen2" color="iconMid" className="narrativeSectionBullet" />
          You can manually assign all section types to any item (regardless of auto settings)
        </div>
        {!isMobile ? (
          <div className="narrativeSectionDescription">
            <Icon icon="statusOpen2" color="iconMid" className="narrativeSectionBullet" />
            Select which sections can be auto-assigned to Narrative Folders and other item types
            <HelpIcon tooltip={autoAssignTooltipText} />
          </div>
        ) : null}
      </ModalDescription>
      {controls}
    </Fragment>
  );
};

const renderNumberingSection = (narrativeNumbering, onNarrativeNumberingChange) => {
  const isDesktop = preprocessorQuery.isDesktop();

  return (
    <Fragment>
      <ModalRow>
        <ModalField flex={6}>
          <FormDropdown
            options={narrativeNumberingOptions()}
            value={narrativeNumbering}
            onChange={onNarrativeNumberingChange}
            title="Numbering System"
            showDescription={true}
            alwaysPortal={!isDesktop}
          />
        </ModalField>
      </ModalRow>
    </Fragment>
  );
};

const SettingsNarrativeGroup = props => {
  const {
    includeNarrative,
    onIncludeNarrativeChange,
    narrativeLabels,
    onNarrativeLabelChange,
    onNarrativeLabelAutoForFolderChange,
    onNarrativeLabelAutoForOtherChange,
    onNarrativeLabelAdd,
    onNarrativeLabelDelete,
    onNarrativeLabelMove,
    strictFolderStructure,
    onStrictFolderStructureChange,
    defaultNarrativeType,
    defaultSequenceType,
    narrativeViewHasItems,
    narrativeNumbering,
    onNarrativeNumberingChange
  } = props;

  const topSection = (
    <Fragment>
      <ModalRow>
        <ModalField flex={6}>
          <FormCheckbox
            label={`Track independent narrative${props.isSyncEnabled ? " (automatically enabled when syncing)" : ""}`}
            checkboxState={includeNarrative}
            disabled={Boolean(props.isSyncEnabled || (includeNarrative && narrativeViewHasItems))}
            onChange={val => onIncludeNarrativeChange(val, defaultNarrativeType, defaultSequenceType)}
          />
        </ModalField>
      </ModalRow>
      <ModalRow>
        <ModalField flex={6}>
          <FormCheckbox
            label={`Enforce strict folder structure (required for Ulysses syncing via Desktop)`}
            checkboxState={strictFolderStructure}
            disabled={Boolean(!includeNarrative || props.isSyncEnabled)}
            onChange={onStrictFolderStructureChange}
            helpTooltip={`With strict ordering, only Narrative Folders can have items nested inside them in the narrative. Regular items (e.g. scenes, events) cannot. This setting is required for syncing to Ulysses, as Ulysses does not allowed Sheets (documents) to have children.`}
          />
        </ModalField>
      </ModalRow>
    </Fragment>
  );

  const children = (
    <Fragment>
      <ModalGroup label="Narrative">{topSection}</ModalGroup>
      {includeNarrative ? (
        <ModalGroup label="Section Labels">
          {renderNarrativeLabelFields(
            narrativeLabels,
            onNarrativeLabelChange,
            onNarrativeLabelAutoForFolderChange,
            onNarrativeLabelAutoForOtherChange,
            onNarrativeLabelAdd,
            onNarrativeLabelDelete,
            onNarrativeLabelMove
          )}
        </ModalGroup>
      ) : null}
      {includeNarrative ? (
        <ModalGroup label="Numbering System">
          {renderNumberingSection(narrativeNumbering, onNarrativeNumberingChange)}
        </ModalGroup>
      ) : null}
    </Fragment>
  );

  return wrapEditingComponent(children);
};

SettingsNarrativeGroup.propTypes = {
  dispatchLocal: PropTypes.func.isRequired,
  includeNarrative: PropTypes.bool.isRequired,
  strictFolderStructure: PropTypes.bool.isRequired,
  isSyncEnabled: PropTypes.bool.isRequired,
  onIncludeNarrativeChange: PropTypes.func.isRequired,
  onStrictFolderStructureChange: PropTypes.func.isRequired,
  narrativeLabels: ImmutablePropTypes.list,
  narrativeNumbering: PropTypes.string.isRequired,
  onNarrativeLabelChange: PropTypes.func.isRequired,
  onNarrativeLabelAutoForFolderChange: PropTypes.func.isRequired,
  onNarrativeLabelAutoForOtherChange: PropTypes.func.isRequired,
  onNarrativeLabelDelete: PropTypes.func.isRequired,
  onNarrativeLabelMove: PropTypes.func.isRequired,
  onNarrativeLabelAdd: PropTypes.func.isRequired,
  enabledNarrativeTypes: ImmutablePropTypes.map.isRequired,
  defaultSequenceType: PropTypes.string.isRequired,
  defaultNarrativeType: PropTypes.string,
  narrativeViewHasItems: PropTypes.bool,
  firstNarrativeType: PropTypeShapes.itemType
};

const getReadOnlyActions = () => {
  return {};
};

const getEditingActions = props => ({
  onIncludeNarrativeChange: (includeNarrative, defaultNarrativeType, defaultSequenceType) => {
    props.dispatchLocal(
      batchOperations.changeIncludeNarrativeAndIncludeType(includeNarrative, defaultNarrativeType, defaultSequenceType)
    );
  },
  onNarrativeLabelChange: (id, name) => {
    props.dispatchLocal(narrativeSettingsOperations.changeLabel(id, name));
  },
  onNarrativeLabelAutoForFolderChange: (id, value) => {
    props.dispatchLocal(narrativeSettingsOperations.changeAutoForFolder(id, value));
  },
  onNarrativeLabelAutoForOtherChange: (id, value) => {
    props.dispatchLocal(narrativeSettingsOperations.changeAutoForOther(id, value));
  },
  onNarrativeLabelDelete: id => {
    props.dispatchLocal(narrativeSettingsOperations.deleteLabel(id));
  },
  onNarrativeLabelAdd: name => {
    props.dispatchLocal(narrativeSettingsOperations.addLabel(name));
  },
  onNarrativeLabelMove: (oldIndex, newIndex) => {
    props.dispatchLocal(narrativeSettingsOperations.moveLabel(oldIndex, newIndex));
  },
  onNarrativeNumberingChange: value => {
    props.dispatchLocal(narrativeSettingsOperations.changeNumbering(value));
  }
});

const mapDispatchToProps = (_dispatch, props) => {
  return mergeDispatchToPropsWithExtraProps(getReadOnlyActions, getEditingActions, props);
};

export default connect(null, mapDispatchToProps)(SettingsNarrativeGroup);
